<!-- Enhanced SuccessNotification.vue -->
<template>
  <div class="notification-overlay">
    <div class="notification-content">
      <div class="success-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clip-rule="evenodd" />
        </svg>
      </div>
      <p class="notification-message">{{ message }}</p>
      <div class="progress-bar">
        <div class="progress-fill" :style="progressStyle"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

interface Props {
  message: string
  duration?: number
}

const props = withDefaults(defineProps<Props>(), {
  duration: 4000
})

const progress = ref(100)
const interval = ref<number | null>(null)

const progressStyle = computed(() => {
  return { width: `${progress.value}%` }
})

onMounted(() => {
  const step = 10
  const intervalDuration = props.duration / (100 / step)

  interval.value = setInterval(() => {
    progress.value -= step
    if (progress.value <= 0 && interval.value) {
      clearInterval(interval.value)
    }
  }, intervalDuration)
})
</script>

<style scoped>
.notification-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  background-color: rgba(0, 0, 0, 0.3);
}

.notification-content {
  background-color: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  min-width: 300px;
  animation: notification-appear 0.3s ease-out;
}

@keyframes notification-appear {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.success-icon {
  width: 64px;
  height: 64px;
  color: #059669;
  animation: tick-animation 0.5s ease-in-out;
}

@keyframes tick-animation {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.notification-message {
  font-size: 1.25rem;
  font-weight: 500;
  color: #1f2937;
  margin: 0;
  text-align: center;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background-color: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #059669;
  transition: width 0.3s linear;
  border-radius: 3px;
}
</style>
